// Shared state templates (DESIGN_SYSTEM §9.1, §9.2 "Empty, error, plan-locked, pending-activation
// panels"): page, card and inline sizes. Illustration-free: a title, one line, one primary action.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import type { ActivationStep, Tone } from "../state";
import { toneClasses } from "../tones";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";

export type PanelKind =
  | "empty"
  | "no-results"
  | "all-done"
  | "not-set-up"
  | "error"
  | "success"
  | "permission-denied"
  | "plan-locked"
  | "pending-activation"
  | "offline";

const look: Record<PanelKind, { icon: IconName; tone: Tone }> = {
  empty: { icon: "inbox", tone: "neutral" },
  "no-results": { icon: "search", tone: "neutral" },
  "all-done": { icon: "check", tone: "success" },
  "not-set-up": { icon: "gear", tone: "neutral" },
  error: { icon: "octagon", tone: "danger" },
  success: { icon: "check", tone: "success" },
  "permission-denied": { icon: "lock", tone: "neutral" },
  "plan-locked": { icon: "lock", tone: "action" },
  "pending-activation": { icon: "clock", tone: "warning" },
  offline: { icon: "pulse", tone: "warning" },
};

export interface PanelAction {
  label: string;
  onClick: () => void;
  "data-testid"?: string;
}

export interface StatePanelProps {
  kind: PanelKind;
  title: ReactNode;
  body?: ReactNode;
  action?: PanelAction;
  secondaryAction?: PanelAction;
  /** For errors: the request ID, shown small for support. */
  requestId?: string;
  /** For pending activation: what's left and who acts. */
  steps?: ActivationStep[];
  size?: "page" | "card" | "inline";
  headingLevel?: 2 | 3;
  className?: string;
  "data-testid"?: string;
}

const actorLabel = (a: ActivationStep["actor"]) =>
  a === "vendor"
    ? t("states.pendingWhoVendor")
    : a === "platform"
      ? t("states.pendingWhoPlatform")
      : t("states.pendingWhoThirdParty");

export function StatePanel({
  kind,
  title,
  body,
  action,
  secondaryAction,
  requestId,
  steps,
  size = "card",
  headingLevel = 2,
  className,
  "data-testid": testId,
}: StatePanelProps) {
  const { icon, tone } = look[kind];
  const H = headingLevel === 2 ? "h2" : "h3";
  const isError = kind === "error";
  // Site look (owner, 2026-10-21): page and card panels are centred, cards use the dashboard card
  // style, and calm kinds (neutral/action) get the brand-tint tile with a gradient icon. Warning,
  // danger and success keep their tone colours so the meaning still reads at a glance.
  const brand = tone === "neutral" || tone === "action";
  const centred = size !== "inline";
  return (
    <div
      role={isError ? "alert" : kind === "success" ? "status" : undefined}
      data-state={kind}
      data-testid={testId}
      className={cx(
        "flex flex-col gap-2",
        size === "page" && "mx-auto max-w-xl items-center py-12 text-center",
        size === "card" &&
          "items-center rounded-card border border-card bg-surface px-5 py-10 text-center shadow-card",
        size === "inline" && "items-start py-2",
        className,
      )}
    >
      <span
        className={cx(
          "flex items-center justify-center",
          size === "inline" ? "size-8 rounded-sm" : "mb-1 size-12 rounded-full",
          brand ? "ady-bg-brand-tint border border-card" : toneClasses[tone].badge,
        )}
      >
        <Icon name={icon} size={size === "inline" ? 18 : 22} gradient={brand} />
      </span>
      <H
        className={cx(
          "font-semibold",
          size === "page" ? "text-title-sm" : size === "inline" ? "text-md" : "text-lg",
        )}
      >
        {title}
      </H>
      {body ? (
        <p className={cx("max-w-prose text-sm text-fg-secondary", centred && "mx-auto")}>{body}</p>
      ) : null}
      {steps && steps.length ? (
        <ol className="flex w-full max-w-prose flex-col gap-2 text-start">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Icon
                name={s.done ? "check" : "clock"}
                size={18}
                className={cx("mt-0.5 flex-none", s.done ? "text-success-fg" : "text-warning-fg")}
              />
              <span className={cx(s.done ? "text-fg-secondary line-through" : "text-fg")}>
                {s.label} <span className="text-fg-tertiary">({actorLabel(s.actor)})</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {action || secondaryAction ? (
        <div className={cx("flex flex-wrap gap-3 pt-2", centred && "justify-center")}>
          {action ? (
            <Button onClick={action.onClick} data-testid={action["data-testid"]}>
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button
              variant="secondary"
              onClick={secondaryAction.onClick}
              data-testid={secondaryAction["data-testid"]}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}
      {isError && requestId ? (
        <p className="text-xs text-fg-tertiary">{t("common.requestId", { id: requestId })}</p>
      ) : null}
    </div>
  );
}

/** EmptyState: the four empty kinds (first use, no results, all done, not set up; IA §6.3). */
export function EmptyState(
  props: Omit<StatePanelProps, "kind"> & {
    kind?: "empty" | "no-results" | "all-done" | "not-set-up";
  },
) {
  return <StatePanel {...props} kind={props.kind ?? "empty"} />;
}

/** Error panel with Retry and the request ID. */
export function ErrorState({
  title = t("states.errorTitle"),
  body = t("states.errorBody"),
  onRetry,
  requestId,
  ...rest
}: Partial<Omit<StatePanelProps, "kind" | "action">> & { onRetry?: () => void }) {
  return (
    <StatePanel
      {...rest}
      kind="error"
      title={title}
      body={body}
      requestId={requestId}
      action={onRetry ? { label: t("common.retry"), onClick: onRetry } : undefined}
    />
  );
}
